import React from 'react';
import type { ReactElement } from 'react';
import { render } from '@wix/cli-ui-kit';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import { BiProvider } from '@wix/cli-telemetry-react';
import type { CommandServices } from '@wix/cli-command-contract';
import type { Command } from '@commander-js/extra-typings';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import { reportCommandStartEvent } from '../bi.js';
import packageJson from '../../package.json' with { type: 'json' };

export async function renderCommand(
  command: Command,
  { biLogger, errorReporter, panorama }: CommandServices,
  tree: ReactElement
) {
  await runCommand(command, { biLogger, errorReporter, panorama }, async () => {
    const headers = buildHttpClientHeaders({
      flow: 'CLI',
      command: command.name(),
      cliVersion: packageJson.version,
    });

    await render(
      <BiProvider value={biLogger}>
        <ErrorReporterProvider value={errorReporter}>
          <PanoramaProvider value={panorama}>
            <HttpClientProvider headers={headers}>{tree}</HttpClientProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
  });
}

export async function runCommand(
  command: Command,
  services: CommandServices,
  commandFn: () => Promise<void>
) {
  const commandStatus = reportCommandStartEvent(services.biLogger, command);
  const transaction = createCommandTransaction(services.panorama, command);

  try {
    transaction.start();
    await commandFn();
    transaction.finish();
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
